import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { ListOrdered, Pencil, PhoneCall, Trash2, UserCheck } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API_BASE } from "@/config/api"
import { formatDate } from "@/lib/format"
import { opsErrorMessage } from "@/lib/ops-error"
import { useEntitlements } from "@/hooks/use-entitlements"
import { toast } from "sonner"

const API = API_BASE

const PRIORITIES = [0, 1, 2]

function statusVariant(status) {
  if (status === "called") return "default"
  if (status === "waiting") return "secondary"
  if (status === "completed") return "outline"
  return "destructive"
}

export default function Queues() {
  const { t } = useTranslation()
  const { hasFeature } = useEntitlements()
  const canNetwork = hasFeature("multi_location")
  const canAdt = hasFeature("adt")
  const [queues, setQueues] = useState([])
  const [summary, setSummary] = useState({ queue_count: 0, waiting_total: 0 })
  const [selected, setSelected] = useState(null)
  const [tickets, setTickets] = useState([])
  const [ticketSummary, setTicketSummary] = useState({})
  const [clients, setClients] = useState([])
  const [departments, setDepartments] = useState([])
  const [allSites, setAllSites] = useState(false)
  const [loading, setLoading] = useState(true)
  const [queueForm, setQueueForm] = useState({ name: "", department: "" })
  const [editingQueueId, setEditingQueueId] = useState(null)
  const [ticketForm, setTicketForm] = useState({ client_id: "", client_name: "", priority: 0 })

  const loadQueues = useCallback(async () => {
    const { data } = await axios.get(`${API}/queues`, { params: allSites ? { all_sites: true } : {} })
    if (Array.isArray(data)) {
      setQueues(data)
      setSummary({
        queue_count: data.length,
        waiting_total: 0,
      })
    } else {
      setQueues(data.queues || [])
      setSummary(data.summary || { queue_count: 0, waiting_total: 0 })
    }
    return data.queues || data || []
  }, [allSites])

  const loadDepartments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ops/department-suggestions`)
      setDepartments(data.departments || [])
    } catch {
      setDepartments([])
    }
  }, [])

  const loadTickets = useCallback(async (queueId) => {
    if (!queueId) {
      setTickets([])
      setTicketSummary({})
      return
    }
    const { data } = await axios.get(`${API}/queues/${queueId}/tickets`)
    if (Array.isArray(data)) {
      setTickets(data)
      setTicketSummary({})
    } else {
      setTickets(data.tickets || [])
      setTicketSummary(data.summary || {})
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const q = await loadQueues()
      await loadDepartments()
      const { data: clientData } = await axios.get(`${API}/clients`)
      setClients(clientData || [])
      if (!selected && q.length) {
        setSelected(q[0].id)
      }
    } catch {
      toast.error(t("app.queues.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [loadQueues, loadDepartments, selected, t])

  useEffect(() => {
    load()
  }, [allSites])

  useEffect(() => {
    loadTickets(selected)
  }, [selected, loadTickets])

  const resetQueueForm = () => {
    setQueueForm({ name: "", department: "" })
    setEditingQueueId(null)
  }

  const saveQueue = async () => {
    if (!queueForm.name.trim()) return toast.error(t("app.queues.toast.queueNameRequired"))
    try {
      if (editingQueueId) {
        await axios.patch(`${API}/queues/${editingQueueId}`, {
          name: queueForm.name.trim(),
          department: queueForm.department.trim(),
        })
        toast.success(t("app.queues.toast.queueUpdated"))
      } else {
        const { data } = await axios.post(`${API}/queues`, {
          name: queueForm.name.trim(),
          department: queueForm.department.trim(),
        })
        setSelected(data.id)
        toast.success(t("app.queues.toast.queueCreated"))
      }
      resetQueueForm()
      await loadQueues()
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.queues.toast.saveFailed"))
    }
  }

  const startEditQueue = (q) => {
    setEditingQueueId(q.id)
    setSelected(q.id)
    setQueueForm({ name: q.name, department: q.department || "" })
  }

  const deleteQueue = async (id) => {
    try {
      await axios.delete(`${API}/queues/${id}`)
      if (selected === id) setSelected(null)
      if (editingQueueId === id) resetQueueForm()
      await load()
      toast.success(t("app.queues.toast.queueDeleted"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.queues.toast.saveFailed"))
    }
  }

  const addTicket = async () => {
    if (!selected) return toast.error(t("app.queues.toast.selectQueue"))
    if (!ticketForm.client_id && !ticketForm.client_name.trim()) {
      return toast.error(t("app.queues.toast.clientRequired"))
    }
    try {
      await axios.post(`${API}/queues/tickets`, {
        queue_id: selected,
        client_id: ticketForm.client_id || null,
        client_name: ticketForm.client_name.trim(),
        priority: Number(ticketForm.priority) || 0,
      })
      setTicketForm({ client_id: "", client_name: "", priority: 0 })
      await Promise.all([loadTickets(selected), loadQueues()])
      toast.success(t("app.queues.toast.ticketAdded"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.queues.toast.saveFailed"))
    }
  }

  const updateTicketStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/queues/tickets/${id}`, { status })
      await Promise.all([loadTickets(selected), loadQueues()])
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.queues.toast.saveFailed"))
    }
  }

  const callNext = async () => {
    if (!selected) return
    try {
      await axios.post(`${API}/queues/${selected}/call-next`)
      await Promise.all([loadTickets(selected), loadQueues()])
      toast.success(t("app.queues.toast.calledNext"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.queues.toast.saveFailed"))
    }
  }

  const removeTicket = async (id) => {
    try {
      await axios.delete(`${API}/queues/tickets/${id}`)
      await Promise.all([loadTickets(selected), loadQueues()])
      toast.success(t("app.queues.toast.ticketRemoved"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.queues.toast.saveFailed"))
    }
  }

  const statusLabel = (status) => t(`app.queues.status.${status}`, { defaultValue: status })

  const selectedQueue = queues.find((q) => q.id === selected)

  return (
    <FeatureGate
      feature="queues"
      title={t("app.queues.gateTitle")}
      description={t("app.queues.gateDescription")}
    >
      <AppLayout title={t("app.queues.title")}>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.queues.intro")}{" "}
          <Link to="/app/adt" className="underline underline-offset-2">
            {t("app.nav.adt")}
          </Link>
          {" · "}
          <Link to="/app/appointments" className="underline underline-offset-2">
            {t("app.nav.appointments")}
          </Link>
        </p>

        <PilotBanner className="mb-4 max-w-2xl">{t("app.queues.pilotHint")}</PilotBanner>

        {canNetwork && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
            <input
              type="checkbox"
              checked={allSites}
              onChange={(e) => setAllSites(e.target.checked)}
            />
            {t("app.ops.allSites", { defaultValue: "Toate locațiile" })}
          </label>
        )}

        <div className="grid sm:grid-cols-2 gap-3 mb-6 max-w-md">
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.queues.metrics.queues")}</p>
            <p className="font-display text-2xl mt-1">{summary.queue_count}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.queues.metrics.waiting")}</p>
            <p className="font-display text-2xl mt-1">{summary.waiting_total}</p>
          </article>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          <div className="space-y-6">
            <section className="rounded-xl border border-border p-6 bg-card space-y-4">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <ListOrdered className="h-4 w-4" />
                {editingQueueId ? t("app.queues.editQueue") : t("app.queues.newQueue")}
              </h3>
              <div>
                <Label className="text-xs">{t("app.queues.fieldQueueName")}</Label>
                <Input
                  className="mt-1"
                  placeholder={t("app.queues.queueNamePlaceholder")}
                  value={queueForm.name}
                  onChange={(e) => setQueueForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">{t("app.queues.fieldDepartment")}</Label>
                <Input
                  className="mt-1"
                  list="ops-departments"
                  placeholder={t("app.queues.departmentPlaceholder")}
                  value={queueForm.department}
                  onChange={(e) => setQueueForm((f) => ({ ...f, department: e.target.value }))}
                />
                <datalist id="ops-departments">
                  {departments.map((d) => (
                    <option key={d} value={d} />
                  ))}
                </datalist>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full" onClick={saveQueue}>
                  {editingQueueId ? t("app.queues.save") : t("app.queues.createQueueBtn")}
                </Button>
                {editingQueueId && (
                  <Button variant="ghost" className="rounded-full" onClick={resetQueueForm}>
                    {t("app.queues.cancel")}
                  </Button>
                )}
              </div>
            </section>

            <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
              {loading && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.queues.loading")}</li>
              )}
              {!loading &&
                queues.map((q) => (
                  <li key={q.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <button
                        type="button"
                        className={`text-left flex-1 min-w-0 rounded-lg px-2 py-1 -mx-2 ${
                          selected === q.id ? "bg-secondary" : "hover:bg-muted"
                        }`}
                        onClick={() => {
                          setSelected(q.id)
                          setTicketForm({ client_id: "", client_name: "", priority: 0 })
                        }}
                      >
                        <span className="font-medium">{q.name}</span>
                        {q.department && (
                          <span className="text-muted-foreground text-xs ml-1">({q.department})</span>
                        )}
                        <span className="text-xs text-muted-foreground block mt-0.5">
                          {t("app.queues.queueCounts", {
                            waiting: q.waiting_count || 0,
                            called: q.called_count || 0,
                          })}
                        </span>
                      </button>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => startEditQueue(q)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("app.queues.deleteQueueTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("app.queues.deleteQueueBody")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("app.queues.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteQueue(q.id)}>
                                {t("app.queues.confirmDelete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </li>
                ))}
              {!loading && !queues.length && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.queues.noQueues")}</li>
              )}
            </ul>

            {selected && (
              <section className="rounded-xl border border-border p-6 bg-card space-y-3">
                <h3 className="text-sm font-medium">{t("app.queues.addTicket")}</h3>
                <div>
                  <Label className="text-xs">{t("app.queues.fieldClient")}</Label>
                  <Select
                    value={ticketForm.client_id || "__walkin__"}
                    onValueChange={(v) =>
                      setTicketForm((f) => ({ ...f, client_id: v === "__walkin__" ? "" : v }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__walkin__">{t("app.queues.walkIn")}</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("app.queues.fieldWalkInName")}</Label>
                  <Input
                    className="mt-1"
                    value={ticketForm.client_name}
                    onChange={(e) => setTicketForm((f) => ({ ...f, client_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("app.queues.fieldPriority")}</Label>
                  <Select
                    value={String(ticketForm.priority)}
                    onValueChange={(v) => setTicketForm((f) => ({ ...f, priority: Number(v) }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p} value={String(p)}>
                          {t(`app.queues.priority.${p}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" className="rounded-full" onClick={addTicket}>
                  {t("app.queues.addTicketBtn")}
                </Button>
              </section>
            )}
          </div>

          <div className="space-y-4">
            {selected && selectedQueue && (
              <div className="flex flex-wrap gap-2 items-center justify-between">
                <h3 className="text-sm font-medium">{selectedQueue.name}</h3>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" asChild>
                    <Link
                      to={`/app/queues/display?queue=${selected}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("app.queues.displayBoardLink")}
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1"
                    onClick={callNext}
                    disabled={!(ticketSummary.waiting > 0)}
                  >
                    <PhoneCall className="h-3.5 w-3.5" />
                    {t("app.queues.callNext")}
                  </Button>
                </div>
              </div>
            )}

            {selected && (
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary">
                  {t("app.queues.badgeWaiting", { count: ticketSummary.waiting || 0 })}
                </Badge>
                <Badge variant="default">
                  {t("app.queues.badgeCalled", { count: ticketSummary.called || 0 })}
                </Badge>
              </div>
            )}

            <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
              {tickets.map((ticket) => (
                <li key={ticket.id} className="px-4 py-3 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{ticket.client_name || t("app.queues.walkIn")}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(ticket.created_at)}
                        {ticket.priority > 0 && ` · ${t("app.queues.priorityLabel", { n: ticket.priority })}`}
                        {ticket.appointment_start && (
                          <>
                            {" · "}
                            {t("app.queues.appointmentTime", {
                              defaultValue: "Programare {{time}}",
                              time: formatDate(ticket.appointment_start),
                            })}
                          </>
                        )}
                      </p>
                    </div>
                    <Badge variant={statusVariant(ticket.status)} className="capitalize shrink-0">
                      {statusLabel(ticket.status)}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ticket.status === "waiting" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full h-8"
                        onClick={() => updateTicketStatus(ticket.id, "called")}
                      >
                        <PhoneCall className="h-3 w-3 mr-1" />
                        {t("app.queues.actionCall")}
                      </Button>
                    )}
                    {ticket.status === "called" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full h-8"
                        onClick={() => updateTicketStatus(ticket.id, "completed")}
                      >
                        <UserCheck className="h-3 w-3 mr-1" />
                        {t("app.queues.actionComplete")}
                      </Button>
                    )}
                    {canAdt && ticket.client_id && (ticket.status === "waiting" || ticket.status === "called") && (
                      <Button size="sm" variant="outline" className="rounded-full h-8" asChild>
                        <Link
                          to={`/app/adt?client=${ticket.client_id}&ticket=${ticket.id}`}
                        >
                          {t("app.queues.admitBtn", { defaultValue: "Internare" })}
                        </Link>
                      </Button>
                    )}
                    {(ticket.status === "waiting" || ticket.status === "called") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full h-8 text-muted-foreground"
                        onClick={() => updateTicketStatus(ticket.id, "cancelled")}
                      >
                        {t("app.queues.actionCancel")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full h-8 text-muted-foreground"
                      onClick={() => removeTicket(ticket.id)}
                    >
                      {t("app.queues.actionRemove")}
                    </Button>
                  </div>
                </li>
              ))}
              {selected && !tickets.length && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.queues.noTickets")}</li>
              )}
              {!selected && (
                <li className="px-4 py-8 text-center text-muted-foreground">
                  {t("app.queues.selectQueueHint")}
                </li>
              )}
            </ul>
          </div>
        </div>
      </AppLayout>
    </FeatureGate>
  )
}
